import React, { useCallback, useState } from "react";

import { Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import { Currency } from "../../../../../../../models/currency";
import { EstimatePrice } from "../../../../../../../models/estimate";
import { RoomJob } from "../../../../../../../models/sales";
import CurrencyService from "../../../../../../../services/currency.service";
import { useAppState } from "../../../../../../../context/StateContext";
import { Status } from "../../../../../../../util/enums";
import {
  formatCurrency,
  getTotalCostsByPrice,
  TotalPriceCost,
} from "../../../../../../../util/prices";
import useStyles from "./styles";
import { getMeasurement } from "../../../../../../../util/getMeasurement";

interface Props {
  prices: EstimatePrice[];
  inventory?: RoomJob[];
}

const SummaryCosting: React.FC<Props> = (props) => {
  const { prices, inventory } = props;

  const classes = useStyles();
  const { t: translate } = useTranslation();

  const { currentUser } = useAppState();
  const currency = currentUser?.currency || currentUser?.company?.currency;
  const measurement = getMeasurement();

  const [allCurrencies, setAllCurrencies] = useState<Currency[]>([]);

  useQuery(
    ["currencies.list"],
    () =>
      CurrencyService.getAll({
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
        status: Status.ACTIVE,
      }),
    {
      enabled: !!currentUser?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array)
          setAllCurrencies(response.data);
      },
    }
  );
  const findSymbol = (key: string) =>
    allCurrencies.find((currency: Currency) => currency.code === key)?.symbol;

  const renderSymbolTotalByPrice = (totalCost: TotalPriceCost) => {
    const { estimate } = totalCost;
    const keys = Object.keys(estimate);
    const values = Object.values(estimate);

    return (
      <Grid item container direction="row" className={classes.widthAuto}>
        <Grid
          item
          container
          direction="column"
          className={classnames(classes.widthAuto, classes.gap4, classes.mr16)}
        >
          {keys.map((key) => (
            <Grid item className={classes.totalPriceSymbolValue} key={key}>
              <span className={classes.totalPriceSymbol}>{findSymbol(key)}</span>
            </Grid>
          ))}
        </Grid>
        <Grid
          item
          container
          direction="column"
          alignItems="flex-end"
          className={classnames(classes.widthAuto, classes.gap4)}
        >
          {values.map((value, index) => {
            const currency = allCurrencies.find((currency) => currency.code === keys[index]);

            return (
              <Grid item key={index}>
                <span className={classes.totalPriceSymbol}>{`${keys[index]}${formatCurrency(
                  value,
                  currency,
                  false
                )}`}</span>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    );
  };

  const renderTotalCostsByPrice = (price?: EstimatePrice) => {
    const totalCost = getTotalCostsByPrice(price, inventory, currency, allCurrencies, measurement);
    const { estimate } = totalCost;

    if (Object.keys(estimate).length > 0) {
      return (
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          className={classes.totalPriceContainer}
        >
          <Grid item>
            <span className={classes.totalPriceTitle}>
              {getTotalPriceTitleBySequence(price?.sequence)}
            </span>
          </Grid>
          {renderSymbolTotalByPrice(totalCost)}
        </Grid>
      );
    }

    return null;
  };

  const getTotalPriceTitleBySequence = useCallback((sequence?: number) => {
    return `${translate("totalOf")} ‘${translate("price")} ${sequence ?? ""}’`;
  }, []);

  return <>{prices?.map((price) => renderTotalCostsByPrice(price))}</>;
};

export default React.memo(SummaryCosting);
